/**
 * Authenticated Layout Component
 * Provides consistent page structure with header containing auth controls
 */

import type { ReactNode } from 'react'
import { makeStyles, tokens } from '@fluentui/react-components'
import { useAuthContext } from '../auth/AuthProvider'
import { SignInButton } from './SignInButton'
import { HeaderProvider, useHeaderLeftContent } from './HeaderContext'
import { DemoModeBanner } from './DemoModeBanner'

interface AuthenticatedLayoutProps {
  children: ReactNode
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  skipLink: {
    position: 'absolute',
    top: '-40px',
    left: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    textDecoration: 'none',
    zIndex: 1000,
    ':focus': {
      top: 0,
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerLeft: {
    flex: 1,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
  },
})

function LayoutContent({ children }: { children: ReactNode }) {
  const styles = useStyles()
  const headerLeftContent = useHeaderLeftContent()
  const { isAuthenticated, silentAuthAttempted, requiresInteraction } = useAuthContext()
  const showUserControls = silentAuthAttempted && isAuthenticated && !requiresInteraction

  return (
    <div className={styles.root}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>
      <header className={styles.header}>
        <div className={styles.headerLeft}>{headerLeftContent}</div>
        {showUserControls && (
          <div className={styles.headerRight}>
            <SignInButton />
          </div>
        )}
      </header>
      <DemoModeBanner />
      <main id="main-content" tabIndex={-1} className={styles.main}>
        {children}
      </main>
    </div>
  )
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  return (
    <HeaderProvider>
      <LayoutContent>{children}</LayoutContent>
    </HeaderProvider>
  )
}
